<template>
  <v-card flat>
    <v-form ref="payment" v-model="validCardDetails" lazy-validation>
      <v-card-title v-if="isEditingCard">
        <span class="subtitle-2">
          Modify
          <span class="font-weight-bold">{{brand}}</span>
          ending
          <span class="font-weight-bold">****{{cardEnding}}</span>
        </span>
        <v-spacer />
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <div v-show="!isEditingCard" class="ma-0 pa-0">
        <v-card-title>
          <span class="subtitle-2">Card Details</span>
        </v-card-title>
        <v-container class="py-0 my-0">
          <v-card-text>
            <div id="add-card-element" class="mt-2">
              <!--Stripe.js injects the Card Element-->
            </div>
            <div id="add-card-errors" class="mt-2 red--text" role="alert"></div>
          </v-card-text>
        </v-container>
      </div>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field
                label="Name on Card"
                dense
                outlined
                v-model="nameOnCard"
                :rules="[required('Name'),maxLength('Name'), minLength('Name')]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field label="Phone" outlined dense v-model="phone" :rules="[isPhoneNumber]"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="py-0" v-if="isEditingCard">
              <v-select
                :items="months"
                v-model="expiryMonth"
                :item-text="expiryMonth"
                label="Exp Month(MM)"
                dense
                outlined
                :rules="[required('Expiry Month'),validMonth, monthIsNotPast]"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" class="py-0" v-if="isEditingCard">
              <v-select
                :items="yearsList"
                :item-text="expiryYear"
                v-model="expiryYear"
                label="Exp Year(YYYY)"
                :rules="[required('Expiry Year'),validYear]"
                dense
                outlined
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-title class="pt-0">
        <span class="subtitle-2">Billing Details</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field
                label="Address Line 1"
                dense
                outlined
                v-model="addressLine1"
                :rules="[required('Address')]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field label="Address Line 2" dense outlined v-model="addressLine2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field label="City" dense outlined v-model="city"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field
                label="Postcode"
                dense
                outlined
                v-model="postcode"
                :rules="[required('Postcode'),isPostCode]"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions v-if="isEditingCard">
        <v-btn color="blue darken-1" text @click="closeDialog">Close</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="blue darken-1"
          outlined
          :disabled="!validCardDetails"
          @click.stop="modifyCardDetails(paymentMethodId)"
          :loading="isProcessing"
        >Save</v-btn>
      </v-card-actions>
      <v-card-actions v-else>
        <v-container class="py-0 my-0">
          <v-btn
            color="blue darken-1"
            outlined
            block
            large
            :disabled="!validCardDetails"
            @click.stop="addNewCard"
            :loading="isProcessing"
          >Save Card</v-btn>
        </v-container>
      </v-card-actions>
    </v-form>
  </v-card>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import checkPostCode from "@/util/validation/postcode.js";
import checkUKTelephone from "@/util/validation/phoneNumber.js";
export default {
  name: "PaymentCard",
  props: {
    isEditingCard: {
      type: Boolean,
      required: true
    },
    paymentMethodId: {
      type: String
    },
    sources: {
      type: Array
    }
  },

  data: () => {
    return {
      validCardDetails: false,
      nameOnCard: "",
      months: [
        "01",
        "02",
        "03",
        "04",
        "05",
        "06",
        "07",
        "08",
        "09",
        "10",
        "11",
        "12"
      ],
      expiryMonth: "",
      expiryYear: "",
      brand: "",
      cardEnding: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      postcode: "",
      phone: "",
      error: "",
      errorMessage: "",
      stripe: null,
      card: null,
      isProcessing: false,
      required(fieldName) {
        return v => (v && v.length > 0) || `${fieldName} Required`;
      },
      maxLength(fieldName) {
        return v =>
          (v && v.length < 25) ||
          `${fieldName} must be less than 25 characters`;
      },
      minLength(fieldName) {
        return v =>
          (v && v.length > 2) || `${fieldName} must be more than 3 characters`;
      },

      isPostCode: val => {
        return checkPostCode(val);
      },
      isPhoneNumber: val => {
        return checkUKTelephone(val);
      }
    };
  },
  computed: {
    ...mapGetters(["retrieveUser"]),
    yearsList() {
      let currentYear = new Date().getFullYear();
      let yearList = [];
      let i = 0;
      while (i < 50) {
        let year = currentYear++;
        yearList.push(year.toString());
        i++;
      }
      return yearList;
    },
    validMonth() {
      return () => this.months.includes(this.expiryMonth) || "Invalid Month";
    },
    validYear() {
      return () => this.yearsList.includes(this.expiryYear) || "Invalid Year";
    },
    monthIsNotPast() {
      return () => {
        if (parseInt(this.expiryYear) == new Date().getFullYear()) {
          let curMonth = new Date().getMonth();
          if (parseInt(this.expiryMonth) < curMonth + 1) {
            return "Expiry Month Invalid";
          }
          return true;
        }
        return true;
      };
    }
  },
  methods: {
    ...mapActions([
      "setSnackbar",
      "updateCardDetailsAction",
      "stripeCardSetupIntentAction"
    ]),

    getCardName(name) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    getCardDetails(card) {
      this.sources.forEach(elem => {
        if (elem.id == card) {
          this.nameOnCard = elem.billing_details.name;
          this.phone = elem.billing_details.phone;
          this.brand = this.getCardName(elem.card.brand);
          this.cardEnding = elem.card.last4;
          this.expiryYear = elem.card.exp_year.toString();
          this.addressLine1 = elem.billing_details.address.line1;
          this.addressLine2 = elem.billing_details.address.line2;
          this.city = elem.billing_details.address.city;
          this.postcode = elem.billing_details.address.postal_code;
          if (elem.card.exp_month < 10) {
            this.expiryMonth = "0" + elem.card.exp_month;
          } else {
            this.expiryMonth = elem.card.exp_month.toString();
          }
        }
      });
    },
    modifyCardDetails(card) {
      this.isProcessing = true;
      const rec = Object.assign(
        {},
        { userId: this.retrieveUser.userNum },
        { sessionKey: this.retrieveUser.sessionKey },
        {
          billing_details: {
            name: this.nameOnCard,
            phone: this.phone,
            billing_address: {
              line1: this.addressLine1,
              line2: this.addressLine2,
              city: this.city,
              postal_code: this.postcode
            },
            methodInfo: {
              exp_month: this.expiryMonth,
              exp_year: this.expiryYear,
              id: card
            }
          }
        }
      );
      this.updateCardDetailsAction(rec).then(response => {
        if (response.success == true) {
          this.setSnackbar({ text: "Card details updated successfully" });
        } else {
          this.setSnackbar({ color: "error", text: response.message });
        }
        //this.getPaymentMethods()
        this.closeDialog();
        this.$emit("card-update");
        this.isProcessing = false;
      });
    },

    addNewCard() {
      this.isProcessing = true;
      if (this.validCardDetails) {
        const rec = Object.assign(
          {},
          { userId: this.retrieveUser.userNum },
          { sessionKey: this.retrieveUser.sessionKey }
        );
        this.stripeCardSetupIntentAction(rec).then(response => {
          if (response.success && response.clientSecret) {
            this.stripe
              .confirmCardSetup(response.clientSecret, {
                payment_method: {
                  card: this.card,
                  billing_details: {
                    address: {
                      line1: this.addressLine1,
                      line2: this.addressLine2,
                      city: this.city,
                      postal_code: this.postcode
                    },
                    email: null,
                    name: this.nameOnCard,
                    phone: this.phone
                  }
                }
              })
              .then(response => {
                if (response.error) {
                  console.log("Error Adding Card");
                  this.setSnackbar({
                    color: "error",
                    text: response.error.message
                  });
                } else {
                  this.setSnackbar({ text: "Card added successfully" });
                  this.$router.push({ name: "UserAccount" });
                }
              });
          } else {
            this.setSnackbar({ color: "error", text: response.message });
          }
          this.isProcessing = false;
        });
        
      }
    },
    closeDialog() {
      this.$emit("card-closed");
    },

    loadStripe() {
      this.stripe = Stripe(this.$store.state.configs.stripe_client_key);
      this.card = this.stripe.elements().create("card", {
        style: {
          base: {
            fontWeight: 400,
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            fontSize: "16px"
          }
        },
        hidePostalCode: true
      });
      this.card.mount("#add-card-element");
      this.card.addEventListener("change", ({ error }) => {
        const displayError = document.getElementById("add-card-errors");
        this.error = "";
        this.errorMessage = "";
        if (error) {
          displayError.textContent = error.message;
          this.validCardDetails = false;
        } else {
          displayError.textContent = "";
          this.validCardDetails = true;
        }
      });
      console.log("Loading Stripe ...");
    }
  },

  mounted() {
    if (this.isEditingCard) {
      this.getCardDetails(this.paymentMethodId);
    } else {
      this.loadStripe();
      this.$refs.payment.reset();
    }
  }
};
</script>